import * as React from 'react';

// API
import apibridge from '../../apibridge';

// UTILS
import { validationErrorsToString, WWCCStyleDisplay, WWCCTextDisplay } from '../../utils';

// PACKAGES
import { useParams } from 'react-router-dom';
import { SwitchTransition, CSSTransition } from 'react-transition-group';
import { Tooltip } from 'reactstrap';

// COMPONENTS
import PageName from '../../components/PageName';
import LoadingSpinner from '../../components/LoadingSpinner';
import ScrollToTopButton from '../../components/ScrollToTopButton';
import ProfileSummary from '../../components/ProfileSummary';
import ProfileLinkedAccounts from '../../components/ProfileLinkedAccounts';
import ProfileOrganisations from '../../components/ProfileOrganisations';
import ProfileComplaints from '../../components/ProfileComplaints';
import ProfileFeedback from '../../components/ProfileFeedback';
import ProfileIncidents from '../../components/ProfileIncidents';
import ProfilePolicies from '../../components/ProfilePolicies';
import ProfileTraining from '../../components/ProfileTraining';
import GlobalModal from '../../components/GlobalModal';
import RemoveFamiliesMemberModal from './RemoveFamiliesMemberModal';
import ChildrenCheckModal from './ChildrenCheckModal';
import Button from '../../components/Button';
import StatusIcon from '../../components/StatusIcon';
import DisplayDateTime from '../../components/DisplayDateTime';

// ICONS
import { ReactComponent as InfoIcon } from '../../assets/svg/Information.svg';

// TYPES
import { RouteParams } from '../../Interfaces';
import {
	FamiliesFetchFamilyMember,
	DomainProfileType,
	WorkingWithChildrenCheckSingleStatusCheckStatusCheckResult,
	WorkingWithChildrenCheckSingleStatusCheckCommand
} from '../../api/models';

// Type for certification items in FamiliesProfile
interface CertificationItem {
	id?: string;
	state?: string;
	certificationNumber?: string;
	certificationType?: string;
	wwccCanPerformSingleStatusCheck?: boolean;
	wwccEligibleToWork?: boolean;
	wwccMessage?: string;
	wwccLastChecked?: string;
	displayName?: string;
	status?: string;
	statusLabel?: string;
}

const FamiliesProfile: React.FC = () => {
	// Error and Loading state
	const [formErrorMessage, setFormErrorMessage] = React.useState<string>('');

	const [isLoading, setIsLoading] = React.useState<boolean>(true);

	// getting the id from params
	const params = useParams<RouteParams>();
	const familiesId = params.id;

	// Set up data state and initial data
	const initialProfileData: FamiliesFetchFamilyMember = {
		id: undefined,
		childAwareId: undefined,
		profileImage: undefined,
		firstName: undefined,
		lastName: undefined,
		email: undefined,
		profileType: undefined,
		canUpdate: undefined,

		certifications: [],
		organisations: [],
		linkedAccounts: [],
		complaints: [],
		feedback: [],
		incidents: [],
		policies: [],
		trainings: []
	};

	const [profileData, setProfileData] = React.useState<FamiliesFetchFamilyMember>(initialProfileData);

	const getFamiliesProfileData = async () => {
		setIsLoading(true);

		const response = await apibridge.getFamiliesProfileData(familiesId);

		if (response.isError) {
			setIsLoading(false);

			setFormErrorMessage(
				response.validationErrors.length
					? validationErrorsToString(response.validationErrors)
					: response.message || 'An error has occurred'
			);
		} else {
			const { result = initialProfileData } = response;

			setIsLoading(false);

			setProfileData(result);
		}
	};

	React.useEffect(() => {
		getFamiliesProfileData();
	}, []);

	const {
		id,
		childAwareId,
		userId,
		email,
		profileImage,
		firstName,
		lastName,
		profileType,

		certifications,
		linkedAccounts,
		organisations,
		complaints,
		feedback,
		incidents,
		policies,
		trainings,

		canUpdate,
		isCurrentMember
	} = profileData;

	const displayProfileName = (profileType?: string) => {
		if (profileType === DomainProfileType.Parent) {
			return 'Parent profile';
		} else if (profileType === DomainProfileType.Student) {
			return 'Child profile';
		} else return 'Profile';
	};

	const displayProfileRole = (profileType?: string) => {
		if (profileType === DomainProfileType.Parent) {
			return <>Parent or Parent Volunteer</>;
		} else if (profileType === DomainProfileType.Student) {
			return <>Child or Student</>;
		} else return null;
	};

	// Maximum number of items per section
	const defaultMaxItems: number = 4;

	//Check if user is a student
	const isStudent = profileType === DomainProfileType.Student;

	// State to manage Modals
	const [showRemoveFamiliesMemberModal, setShowRemoveFamiliesMemberModal] = React.useState<boolean>(false);

	const [showChildrenCheckModal, setShowChildrenCheckModal] = React.useState<boolean>(false);

	// State to manage Document Id
	const [documentId, setDocumentId] = React.useState<string>('');

	// ===== WWCC API Logic =====
	const [WWCCTooltipOpen, setWWCCTooltipOpen] = React.useState<{ [key: number]: boolean }>({});
	const toggleWWCCTooltip = (idx: number) => {
		setWWCCTooltipOpen((prev) => ({ ...prev, [idx]: !prev[idx] }));
	};

	// State to manage WWCC Status functions
	const [isLoadingWWCCStatusCheck, setIsLoadingWWCCStatusCheck] = React.useState<{ [key: string]: boolean }>({});
	const [showWWCCStatusError, setShowWWCCStatusError] = React.useState<{ [key: string]: boolean }>({});

	// Initial State for WWCC Status - now a map of certification ID to status
	const initialWWCCStatus: { [key: string]: WorkingWithChildrenCheckSingleStatusCheckStatusCheckResult } = {};

	// Main State for WWCC Status - now stores multiple statuses
	const [WWCCStatus, setWWCCStatus] = React.useState<{
		[key: string]: WorkingWithChildrenCheckSingleStatusCheckStatusCheckResult;
	}>(initialWWCCStatus);

	// Function to check WWCC Status for a specific certification
	const confirmCheckWWWCStatus = async (certificationId: string, certificationData: CertificationItem) => {
		setIsLoadingWWCCStatusCheck((prev) => ({ ...prev, [certificationId]: true }));
		setShowWWCCStatusError((prev) => ({ ...prev, [certificationId]: false }));

		const submitData: WorkingWithChildrenCheckSingleStatusCheckCommand = {
			certificationReviewId: certificationId,
			state: certificationData.state || '',
			referenceNumber: certificationData.certificationNumber || '',
			surname: profileData?.lastName || ''
		};

		const response = await apibridge.postWorkingwithchildrencheckSinglestatuscheck(submitData);

		if (response.isError) {
			setShowWWCCStatusError((prev) => ({ ...prev, [certificationId]: true }));
		} else {
			// Type assertion after checking isError is false
			const successResponse = response as { result?: WorkingWithChildrenCheckSingleStatusCheckStatusCheckResult };
			if (successResponse.result) {
				setWWCCStatus((prev) => ({
					...prev,
					[certificationId]: successResponse.result as WorkingWithChildrenCheckSingleStatusCheckStatusCheckResult
				}));
			}
		}

		setIsLoadingWWCCStatusCheck((prev) => ({ ...prev, [certificationId]: false }));
	};

	// Initialize WWCCStatus with existing certification data on component mount
	React.useEffect(() => {
		if (certifications) {
			const updatedStatus: { [key: string]: WorkingWithChildrenCheckSingleStatusCheckStatusCheckResult } = {};

			certifications.forEach((item) => {
				if (item.certificationType === 'WorkingWithChildrenCheck' && item.id) {
					// Always update with the latest data from certificationJoin
					updatedStatus[item.id] = {
						isSuccess: false,
						eligibleToWork: item.wwccEligibleToWork,
						message: item.wwccMessage || '',
						expiryDate: '',
						checkDate: item.wwccLastChecked || ''
					};
				}
			});

			// Update WWCCStatus with the latest data, preserving any existing status checks
			setWWCCStatus((prev) => ({
				...prev,
				...updatedStatus
			}));
		}
	}, [certifications]);

	return (
		<div className="page-general-layout">
			{!!formErrorMessage ? (
				<div className="form-validation-error">{formErrorMessage}</div>
			) : (
				<SwitchTransition mode="out-in">
					<CSSTransition key={`key_${isLoading}`} timeout={600} classNames="standalone-fade" mountOnEnter unmountOnExit>
						{isLoading ? (
							<LoadingSpinner show={isLoading} />
						) : (
							<>
								<PageName title={displayProfileName(profileType)} showBack={true} />

								<ProfileSummary
									profileImage={profileImage}
									firstName={firstName}
									lastName={lastName}
									childAwareId={childAwareId}
									email={email}
									isStudent={isStudent}
								/>

								{/* Remove Family Member */}
								{canUpdate && isCurrentMember && (
									<section className="extra-details no-border">
										<div className="detail-left"></div>

										<div className="detail-right">
											<div className="remove-staff-container">
												<Button
													className="btn-primary remove-staff-btn"
													onClick={() => setShowRemoveFamiliesMemberModal(true)}
												>
													{isStudent ? 'Remove Child from Organisation' : 'Remove Parent from Organisation'}
												</Button>
											</div>
										</div>
									</section>
								)}

								{/* ACCOUNT INFO */}
								<section className="info-section">
									<div className="section-header">
										<h2 className="header-name color-dark">Role</h2>
									</div>

									<div className="row-container">
										<div className="row detail-row">
											<div className="col-11 pdl-0">
												<div className="row detail-info">
													<div className="col-lg-5 pdl-0 detail-name bold">Organisation role</div>

													<div className="col-lg-7 pdl-0 detail-status">
														<div className="title">Role</div>

														<div className="name">{displayProfileRole(profileType)}</div>
													</div>
												</div>
											</div>
										</div>
									</div>

									{profileType === DomainProfileType.Parent && certifications && certifications.length > 0 ? (
										<section className="info-section">
											<div className="section-header">
												<h2 className="header-name color-dark">Documents</h2>
											</div>

											{certifications.map((item, index) => {
												const tooltipId = `wwcc-tooltip-${index}`;

												// Extract WWCC status data for this item
												const wwccStatus = item.id ? WWCCStatus[item.id] : null;

												const eligibleToWork = wwccStatus?.eligibleToWork ?? item.wwccEligibleToWork;
												const message = wwccStatus?.message ?? item.wwccMessage;
												const lastChecked = wwccStatus?.checkDate ?? item.wwccLastChecked;

												return (
													<div key={index} className="row-container">
														<div className="row detail-row">
															<div className="col-1 col-md-auto pdl-0 pdr-0">
																<StatusIcon statusType={item.status || 'Error'} />
															</div>

															<div className="col-11 col-md-auto col-lg-9 full-width">
																<div className="row detail-info">
																	<div className="col-lg-4 detail-name">{item.displayName}</div>

																	<div className="col-lg-8 detail-status">
																		<span className={`label ${item.status?.toLowerCase()}`}>{item.statusLabel}</span>

																		{item.certificationType === 'WorkingWithChildrenCheck' &&
																		item.wwccCanPerformSingleStatusCheck ? (
																			<div className="d-flex justify-content-start flex-wrap mt-1 gap-1 flex-shrink-1">
																				<div className="d-flex gap-2 align-items-center justify-content-start">
																					<p className={`mb-0 body-medium fw-medium ${WWCCStyleDisplay(eligibleToWork)}`}>
																						Eligible to work: {WWCCTextDisplay(eligibleToWork)}
																					</p>

																					{message ? (
																						<>
																							<InfoIcon className="info-icon mt-0" id={tooltipId} />

																							<Tooltip
																								placement="top"
																								isOpen={!!WWCCTooltipOpen[index]}
																								autohide={false}
																								target={tooltipId}
																								toggle={() => toggleWWCCTooltip(index)}
																							>
																								<div className="tooltip-content">{message}</div>
																							</Tooltip>
																						</>
																					) : null}
																				</div>

																				<div className="d-flex align-items-center gap-2">
																					<>
																						<p className="body-medium text-grey-4 mb-0">Last checked:</p>

																						<p className={`body-medium ml-1 ${WWCCStyleDisplay(eligibleToWork)}`}>
																							{lastChecked ? (
																								<DisplayDateTime format="DD/MM/YYYY, hh:mma">
																									{lastChecked}
																								</DisplayDateTime>
																							) : (
																								<>Not checked yet</>
																							)}
																						</p>
																					</>
																				</div>
																			</div>
																		) : null}
																	</div>
																</div>
															</div>

															<div className="col-auto pdr-0 link-group">
																{item.certificationType === 'WorkingWithChildrenCheck' &&
																	item.wwccCanPerformSingleStatusCheck && (
																		<div className="check-status-wrapper mr-2">
																			{isLoadingWWCCStatusCheck[item.id || ''] ? (
																				<div className="d-flex align-items-center gap-2">
																					<span className="text-grey-5 font-weight-bold">Processing...</span>

																					<LoadingSpinner show={true} isStatic width={18} height={18} />
																				</div>
																			) : (
																				<button
																					className="btn-secondary btn-check-status"
																					onClick={() => item.id && confirmCheckWWWCStatus(item.id, item)}
																				>
																					Check Status
																				</button>
																			)}
																		</div>
																	)}

																<Button
																	className="btn-text mt-0"
																	type="button"
																	onClick={() => {
																		setShowChildrenCheckModal(true);
																		setDocumentId(item.id || '');
																	}}
																>
																	View
																</Button>
															</div>

															{showWWCCStatusError[item.id || ''] && (
																<div className="col-12 pdr-0">
																	<div className="d-flex flex-column align-items-end">
																		<p className="body-small fw-medium text-alert-warning mt-2 mb-0 text-right">
																			Something went wrong.
																		</p>
																		<p className="body-small fw-medium text-alert-warning mb-0 text-right">
																			Please try again in a few minutes
																		</p>
																	</div>
																</div>
															)}
														</div>
													</div>
												);
											})}
										</section>
									) : null}

									{profileType === DomainProfileType.Parent && (
										<>
											{linkedAccounts && linkedAccounts.length > 0 && (
												<>
													<br />
													<br />

													<div className="section-header">
														<h2 className="header-name color-dark">Children</h2>
													</div>

													{linkedAccounts.map((item, index) => {
														return (
															<div key={index} className="row-container">
																<div className="row detail-row">
																	<div className="col-11 pdl-0">
																		<div className="row detail-info">
																			<div className="col-lg-5 pdl-0 detail-name bold">{item.name}</div>

																			<div className="col-lg-7 pdl-0 detail-status">
																				<div className="title">Role</div>

																				<div className="name">Parent or Parent Volunteer</div>
																			</div>
																		</div>
																	</div>
																</div>
															</div>
														);
													})}
												</>
											)}
										</>
									)}
								</section>

								{/* ORGANISATIONS */}
								{profileType !== DomainProfileType.Student && (
									<ProfileOrganisations
										organisations={organisations}
										isStudent={profileType === DomainProfileType.Student}
									/>
								)}

								{/* LINKED ACCOUNT */}
								<ProfileLinkedAccounts linkedAccounts={linkedAccounts} userId={userId} />

								{/* COMPLAINTS */}
								<ProfileComplaints complaints={complaints} defaultMaxItems={defaultMaxItems} />

								{/* FEEDBACK */}
								<ProfileFeedback feedback={feedback} defaultMaxItems={defaultMaxItems} />

								{/* INCIDENTS REPORT */}
								<ProfileIncidents incidents={incidents} defaultMaxItems={defaultMaxItems} />

								{/* POLICIES */}
								<ProfilePolicies policies={policies} defaultMaxItems={defaultMaxItems} />

								{/* TRAINING*/}
								<ProfileTraining training={trainings} defaultMaxItems={defaultMaxItems} />

								<GlobalModal
									isOpen={showRemoveFamiliesMemberModal}
									setIsOpen={setShowRemoveFamiliesMemberModal}
									titleText="Remove from Organisation"
								>
									<RemoveFamiliesMemberModal
										familiesMemberId={id}
										getFamiliesProfileData={getFamiliesProfileData}
										firstName={firstName}
										lastName={lastName}
									/>
								</GlobalModal>

								<GlobalModal
									isOpen={showChildrenCheckModal}
									titleText="Working With Children Check"
									setIsOpen={setShowChildrenCheckModal}
									functionOnClose={getFamiliesProfileData}
								>
									<ChildrenCheckModal
										getFamiliesProfileData={getFamiliesProfileData}
										canUpdate={canUpdate}
										documentId={documentId}
										profileData={profileData}
									/>
								</GlobalModal>

								<ScrollToTopButton extraClass="mtop-40" />
							</>
						)}
					</CSSTransition>
				</SwitchTransition>
			)}
		</div>
	);
};

export default FamiliesProfile;
